import { isStudioBindingSelectable, selectableStudioBindings } from 'src/components/star-grid/utils/studioBindings';
import type { StudioDataset, StudioDeck, StudioDimension } from 'src/components/star-grid/reportStudio.types';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';

const bindingUsage = (ids: readonly string[], deck: StudioDeck, text: StarGridText): string => {
  const bindings = new Set(ids);
  return [
    { fields: deck.rows, label: text('studioRows') },
    { fields: deck.filters, label: text('studioPaletteFilter') }
  ]
    .filter(({ fields }) => fields.some((field) => bindings.has(field.binding_id)))
    .map(({ label }) => label)
    .join(' / ');
};
const dimensionOptions = (dataset: StudioDataset): readonly StudioDimension[] =>
  dataset.dimensions ?? selectableStudioBindings(dataset).map((binding) => ({ id: binding.id, label: binding.label, binding_ids: [binding.id] }));

const paletteDimension = (dimension: StudioDimension, dataset: StudioDataset, deck: StudioDeck, text: StarGridText) => {
  const ids = new Set(dimension.binding_ids);
  const bindings = dataset.bindings.filter((binding) => ids.has(binding.id));
  const usable = bindings.filter((binding) => isStudioBindingSelectable(dataset, binding.id));
  return {
    ...dimension,
    bindingId: usable[0]?.id,
    usage: bindingUsage(dimension.binding_ids, deck, text),
    memberCount: usable[0]?.member_count?.toLocaleString() ?? '—',
    // Listed either way; a dimension the source cannot serve says so rather than disappearing.
    unavailableReason: usable.length ? undefined : text(bindings.length ? 'studioDimensionNotInDataset' : 'studioDimensionUnavailable'),
    availabilityLabel: text('studioDimensionAvailability').replace('{dimension}', () => dimension.label),
    searchText: [dimension.label, ...bindings.map((binding) => `${binding.label} ${binding.dimension} ${binding.hierarchy}`)].join(' ').toLowerCase()
  };
};

export const studioPalette = (dataset: StudioDataset, deck: StudioDeck, search: string, text: StarGridText) => {
  const query = search.trim().toLowerCase();
  const available = dimensionOptions(dataset);
  const dimensions = available
    .map((dimension) => paletteDimension(dimension, dataset, deck, text))
    .filter((dimension) => dimension.searchText.includes(query));
  return {
    dimensions,
    count: text('studioPaletteCount').replace('{visible}', String(dimensions.length)).replace('{total}', String(available.length)),
    placeholder: text('studioPaletteSearch').replace('{count}', String(available.length))
  };
};
